import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  viewSubscriptionPlan,
  SubscriptionPlan,
} from '../api/viewSubscriptionPlanApiService';
import {
  getStatusLabel as getSharedStatusLabel,
  getStatusBadgeClasses as getSharedStatusBadgeClasses,
} from '@/shared/utils/statusBadge';

// ============================================================================
// Constants
// ============================================================================

export const PLAN_NAV_ITEMS = [
  { id: 'PodstawoweInformacje', label: 'Podstawowe Informacje' },
  { id: 'CennikIRozliczenia', label: 'Cennik i rozliczenia' },
  { id: 'LimityUzywania', label: 'Limity użytkowania' },
] as const;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Gets the status label in Polish
 * Re-exported from shared utils for convenience
 */
export const getStatusLabel = (
  status: SubscriptionPlan['status'] | null | undefined
): string => {
  return getSharedStatusLabel(status);
};

/**
 * Gets status badge classes based on status
 * Re-exported from shared utils for convenience
 */
export const getStatusBadgeClasses = (
  status: SubscriptionPlan['status'] | null | undefined
): { container: string; dot: string } => {
  return getSharedStatusBadgeClasses(status);
};

// ============================================================================
// Hook Return Type
// ============================================================================

export interface UseViewSubscriptionPlanReturn {
  // Data state
  planData: SubscriptionPlan | undefined;
  plan: SubscriptionPlan | undefined;
  isLoading: boolean;
  showErrorScreen: boolean;

  // Computed values
  statusLabel: string;
  statusBadgeClasses: { container: string; dot: string };

  // Actions
  refetch: () => Promise<void>;
}

// ============================================================================
// useViewSubscriptionPlan Hook
// ============================================================================

/**
 * Custom hook for managing ViewSubscriptionPlan page state and logic
 * Handles fetching plan data, error states, and computed values
 */
export const useViewSubscriptionPlan = (
  planId: string
): UseViewSubscriptionPlanReturn => {
  // State
  const [planData, setPlanData] = useState<SubscriptionPlan | undefined>(
    undefined
  );
  const [showErrorScreen, setShowErrorScreen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Fetch plan data function
  const fetchPlanData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await viewSubscriptionPlan({ planId });
      if (response.success && response.data) {
        setPlanData(response.data);
        setShowErrorScreen(false);
      } else {
        setShowErrorScreen(true);
        console.error('Error fetching plan data:', response.error);
      }
    } catch (error) {
      setShowErrorScreen(true);
      console.error('Error fetching plan data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [planId]);

  // Fetch plan data on mount and when planId changes
  useEffect(() => {
    fetchPlanData();
  }, [fetchPlanData]);

  // Computed values
  const plan = useMemo(() => {
    if (!planData) {
      return undefined;
    }
    if (planData.offer || !planData.offerId) {
      return planData;
    }
    return {
      ...planData,
      offer: {
        offerId: planData.offerId,
        offerName: '',
      },
    };
  }, [planData]);

  const statusLabel = useMemo(
    () => getStatusLabel(plan?.status),
    [plan?.status]
  );

  const statusBadgeClasses = useMemo(
    () => getStatusBadgeClasses(plan?.status),
    [plan?.status]
  );

  return {
    // Data state
    planData,
    plan,
    isLoading,
    showErrorScreen,

    // Computed values
    statusLabel,
    statusBadgeClasses,

    // Actions
    refetch: fetchPlanData,
  };
};
